import React from "react";
import PropTypes from "prop-types";
import {
	SortableContainer,
	SortableElement,
	arrayMove,
} from "react-sortable-hoc";
import _ from "lodash";
import { getLocationByID, setLocationsQuery, capitalise } from "../helpers";

import ErrorList from "./ErrorList";
import Location from "./Location";

/**
 * -Locations-
 * Locations component
 **/
class Locations extends React.Component {
	state = {
		errors: [],
		maxWaypoints: 10,
		clearLocations: null,
	};

	setLocation = (index, lat, lng, value, type) => {
		this.props.setLocation(index, lat, lng, value, type);

		setTimeout(() => {
			setLocationsQuery(this.props.locations);
		}, 1);
	};

	removeLocation = (index) => {
		this.props.removeLocation(index);

		setTimeout(() => {
			setLocationsQuery(this.props.locations);
		}, 1);
	};

	handleClearOptions = (inputStatus) => {
		this.setState({
			clearLocations: inputStatus,
		});
	};

	autoCompleteReady = (complete = false) => {
		this.setState({
			autoCompleteReady: complete,
		});
	};

	getLocationsQuery = () => {
		const userLocationQuery = this.props.location.query.locations; // saved from user entering in form
		const savedLocationQuery = this.props.location.query.location; // saved location from back end

		// User
		if (userLocationQuery) {
			const locations = JSON.parse(userLocationQuery);

			locations.sort((a, b) => {
				if (a.type === 'from') return -1;
				if (b.type === 'from') return 1;
				if (a.type === 'to') return 1;
				if (b.type === 'to') return -1;
				return 0;
			});

			this.props.sortLocations(locations);

			// Saved
		} else if (savedLocationQuery) {
			const locationID = JSON.parse(savedLocationQuery);

			getLocationByID(locationID)
				.then((response) => {
					// Clear other locations first
					const locations = [{}, {}];

					this.props.sortLocations(locations);

					let { lat, lng, name } = response[0];
					name = capitalise(name);

					const locationIndex = response[0].is_start ? 0 : 1;

					this.props.setLocation(locationIndex, lat, lng, name);
				})
				.catch((error) => {
					this.props.addError(error, "plannerDirections");
				});

			// Clear
		} else {
			const locations = [{}, {}];

			this.props.sortLocations(locations);

			this.props.clearEmptyLocations();
		}
	};

	addDestination = () => {
		const waypoints = this.props.locations.length - 2;

		if (waypoints < this.state.maxWaypoints) {
			this.props.addLocation(0, 0, '', 'waypoint');
		} else {
			this.props.addError("Maximum waypoints reached", "plannerDirections");
		}
	};

	toggleLocations = () => {
		const newOrder = arrayMove(this.props.locations, 0, 1);
		this.props.sortLocations(newOrder);
		setLocationsQuery(newOrder);
	};

	onSortEnd = ({ oldIndex, newIndex }) => {
		const newOrder = arrayMove(this.props.locations, oldIndex, newIndex);
		this.props.sortLocations(newOrder);
		setLocationsQuery(newOrder);
	};

	componentDidMount() {
		this.getLocationsQuery();
	}

	componentDidUpdate(prevProps, prevState) {
		const newLocationURL = !_.isEqual(
			this.props.location.query.locations,
			prevProps.location.query.locations
		);

		if (newLocationURL) {
			this.getLocationsQuery();
		}

		if (this.state.autoCompleteReady !== prevState.autoCompleteReady) {
			const pacs = [...document.querySelectorAll(".pac-location-root")];

			pacs.forEach((item, key) => {
				item.id = `pac_${key}`;
			});

			this.setState({
				autoCompleteReady: false,
			});
		}
	}

	componentWillUnmount() {
		this.props.clearEmptyLocations();
	}

	render() {
		return (
			<div className="locations">
				<LocationsList
					locations={this.props.locations}
					setLocation={this.setLocation}
					autoCompleteReady={this.autoCompleteReady}
					removeLocation={this.removeLocation}
					reportError={this.props.addError}
					toggleLocations={this.toggleLocations}
					errors={this.props.errors}
					handleClearOptions={this.handleClearOptions}
					onSortEnd={this.onSortEnd}
					lockAxis="y"
					useDragHandle={true}
					helperClass={`locations-sortable`}
				/>

				{this.props.locations.length > 1 && (
					<AddDestination addDestination={this.addDestination} locations={this.props.locations} />
				)}

				<button
					className="btn btn-small btn-alt clear-icon"
					onClick={() => {
						this.props.clearAllLocations();
					}}
					type="button"
					disabled={this.state.clearLocations !== "true"}
				>
					<span>Clear</span>
					<svg className="icon" aria-hidden="true">
						<use xlinkHref="#close"></use>
					</svg>
				</button>

				{this.props.errors.plannerDirections && (
					<ErrorList errors={this.props.errors.plannerDirections} />
				)}
			</div>
		);
	}
}

const LocationsList = SortableContainer(
	({
		locations,
		setLocation,
		autoCompleteReady,
		removeLocation,
		reportError,
		toggleLocations,
		errors,
		handleClearOptions,
	}) => {
		return (
			<div className="locations-list">
				{locations.map((location, i) => (
					<LocationsItem
						locations={locations}
						locationIndex={i}
						index={i}
						setLocation={setLocation}
						autoCompleteReady={autoCompleteReady}
						removeLocation={removeLocation}
						reportError={reportError}
						errors={eval("errors.locations_" + i)} // eslint-disable-line no-eval
						handleClearOptions={handleClearOptions}
						key={i}
					/>
				))}

				{locations.length === 2 && (
					<button
						className="locations-toggle"
						onClick={toggleLocations}
						type="button"
					>
						<span className="assistive-text">Reverse journey direction</span>
						<svg className="icon" aria-hidden="true">
							<use xlinkHref="#arrow_up-down_alt"></use>
						</svg>
					</button>
				)}
			</div>
		);
	}
);

const LocationsItem = SortableElement(
	({
		locations,
		locationIndex,
		setLocation,
		autoCompleteReady,
		removeLocation,
		reportError,
		errors,
		handleClearOptions,
	}) => {
		return (
			<Location
				locations={locations}
				index={locationIndex}
				setLocation={setLocation}
				autoCompleteReady={autoCompleteReady}
				removeLocation={removeLocation}
				reportError={reportError}
				errors={errors}
				handleClearOptions={handleClearOptions}
			/>
		);
	}
);

const AddDestination = ({ addDestination, locations }) => {
	let disabled = true;

	// check locations is 2 and they are not empty objects
	if (locations.length >= 1 && (!_.isEmpty(locations[0]) || !_.isEmpty(locations[1]))) {
		disabled = false;
	} else {
		disabled = true;
	}

	return (
		<button
			className="add-destination"
			onClick={() => addDestination()}
			type="button"
			aria-label="Add destination" /* label exists as text is capitalised and reads as A-D-D */
			disabled={disabled}
		>
			<svg className="icon" aria-hidden="true">
				<use xlinkHref="#icon_plus_alt2"></use>
			</svg>
			<span>Add destination</span>
		</button>
	);
};

Locations.propTypes = {
	locations: PropTypes.array,
};

Locations.contextTypes = {
	router: PropTypes.object,
};

export default Locations;



// WEBPACK FOOTER //
// ./src/components/Locations.js